import {Box, ButtonBase, Typography} from '@mui/material';
import {FC} from 'react';
import {useIntl} from 'react-intl';
import {
    badgeSx,
    cardSx,
    headerSx,
    pillDotSx,
    pillLabelSx,
    pillsSx,
    pillSx,
    pillValueSx,
    subtitleSx,
    tileActionSx,
    tileDescriptionSx,
    tileHeadSx,
    tileLabelSx,
    tilesSx,
    tileSx,
    tileValueSx,
    titleSx,
} from './StatsStrip.sx';
import {StatsStripType} from './StatsStrip.types';

/**
 * The counts a list page opens with: one card, a row of tiles, and a header that can carry a second
 * axis as pills.
 * <p>
 * Every list page wears the same block; what differs between them is which filters the tiles stand
 * for. So the shape lives here and the pages supply the cells — rather than each page growing its
 * own strip, which is how five pages end up with five spacings and four shades of red.
 */
export const StatsStrip: FC<StatsStripType> = ({icon, title, subtitle, pills, tiles}) => {
    const intl = useIntl();

    return (
        <Box sx={cardSx}>
            <Box sx={headerSx}>
                <Box sx={badgeSx('primary')}>{icon}</Box>
                <Box>
                    <Typography sx={titleSx}>{intl.formatMessage({id: title})}</Typography>
                    {subtitle && <Typography sx={subtitleSx}>{subtitle}</Typography>}
                </Box>

                {Boolean(pills?.length) && (
                    <Box sx={pillsSx}>
                        {pills?.map((pill) => (
                            <ButtonBase
                                key={pill.key}
                                component="div"
                                disableRipple={!pill.onClick}
                                onClick={pill.onClick}
                                sx={pillSx(pill.tone, Boolean(pill.onClick), Boolean(pill.active))}
                            >
                                <Box sx={pillDotSx(pill.tone)}/>
                                <Typography sx={pillLabelSx}>
                                    {intl.formatMessage({id: pill.label})}
                                </Typography>
                                <Typography sx={pillValueSx(pill.tone)}>
                                    {pill.value ?? '—'}
                                </Typography>
                            </ButtonBase>
                        ))}
                    </Box>
                )}
            </Box>

            <Box sx={tilesSx(tiles.length)}>
                {tiles.map((tile, index) => (
                    <ButtonBase
                        key={tile.key}
                        component="div"
                        disableRipple={!tile.onClick}
                        onClick={tile.onClick}
                        sx={tileSx(index, tiles.length, Boolean(tile.onClick), Boolean(tile.active))}
                    >
                        <Box sx={tileHeadSx}>
                            <Box sx={badgeSx(tile.tone)}>{tile.icon}</Box>
                            <Typography sx={tileLabelSx}>
                                {intl.formatMessage({id: tile.label})}
                            </Typography>
                        </Box>
                        {tile.description ? (
                            <Typography sx={tileDescriptionSx}>
                                {intl.formatMessage({id: tile.description})}
                            </Typography>
                        ) : (
                            <Typography sx={tileValueSx(tile.tone, Boolean(tile.emphasise))}>
                                {tile.value ?? '—'}
                            </Typography>
                        )}
                        {tile.actionLabel && (
                            <Typography sx={tileActionSx}>
                                {intl.formatMessage({id: tile.actionLabel})}
                            </Typography>
                        )}
                    </ButtonBase>
                ))}
            </Box>
        </Box>
    );
};
